/* eslint-disable @next/next/no-img-element */
import React, { useEffect, useState } from "react";
import { StyledDiv } from "./blogPostHero.styles";
import { IBlogPage } from "../../types";
import { Section } from "../section/section.comp";
import dayjs from "dayjs";
import { defineCustomElements as deckDeckGoHighlightElement } from "@deckdeckgo/highlight-code/dist/loader";
import { Banner } from "../banner/banner.comp";
import { CustomLink } from "../link/link.comp";
import Link from "next/link";
import Image from "next/image";
import { CommonNinjaWidget } from "commonninja-react";
import { CSSVars } from "../../styles/styles.config";
import { TrofeoBanner } from "../trofeoBanner/trofeoBanner.comp";

// FIFA World Cup articles where the Trofeo prediction-pool banner is shown.
const TROFEO_FIFA_SLUGS = new Set([
  "the-best-brackets-maker-for-the-fifa-world-cup",
  "fifa-world-cup-bracket-format-explained",
  "fifa-world-cup-predictions-contenders-dark-horses",
  "how-to-run-a-fifa-world-cup-bracket-pool",
]);

// Code highlighter
deckDeckGoHighlightElement();

interface IBlogPostHeroProps {
  blogPost: IBlogPage;
  suggestions: IBlogPage[];
}

export const BlogPostHero = ({ blogPost, suggestions = [] }: IBlogPostHeroProps) => {
  const [postContent, setPostContent] = useState(blogPost.postContent);
  const [h2Tags, setH2Tags] = useState<string[]>([]);
  const [activeAnchor, setActiveAnchor] = useState('');

  function handleAffiliateLinkClick(event: any) {
    const affiliateUrl = event.target.getAttribute('data-href');
    if (affiliateUrl) {
      const newTab = window.open('', '_blank');
      if (newTab) {
        newTab.opener = null;
        const newLink = newTab.document.createElement('a');
        newLink.href = affiliateUrl;
        newLink.rel = 'nofollow';
        newTab.document.body.appendChild(newLink);
        newLink.click();
        newTab.document.body.removeChild(newLink);
      }
    }
  };

  function extractH2TagsFromHtml() {
    const allTags = [] as any;

    const formattedPost = blogPost.postContent.map((section) => {
      if (section.type === 'rich-text' || section.type === 'code') {
        const parser = new DOMParser();
        const doc = parser.parseFromString(section.content || '', 'text/html');
        const h2Elements = doc.querySelectorAll('h2');
        const aTagElements = doc.querySelectorAll('a');

        h2Elements.forEach((h2, index) => {
          const textContent = h2.textContent?.replaceAll(' ', '-').replace(/^\s*[\r\n]/gm, "").replace(/\n/g, "").replace(/^--/gm, "");;
          if (textContent) {
            h2.setAttribute('id', `${textContent}`);
            allTags.push(textContent);
          }
        });

        aTagElements.forEach((a, index) => {
          const { hostname, protocol } = new URL(a.href);

          if (a.href.includes('cloak')) {
            a.setAttribute('data-href', a.href.replace('cloak', ''));
            a.setAttribute('href', `${protocol}//${hostname}`);
            a.setAttribute('rel', 'nofollow');
          }
        });

        return { ...section, content: doc?.documentElement?.innerHTML || '' };
      }

      return section;
    });
    setPostContent(formattedPost);
    setH2Tags(allTags);
  }

  function renderMightLikeSection() {
    return (
      <div className="might-like">
        <h3> You Might Also Like:</h3>
        <div className="post-cards-wrapper">
          {suggestions.map((post: IBlogPage) => (
            <Link
              key={post.slug}
              title={`${post.name}`}
              href={`/blog/${post.slug}`}
            >
              <div className="post-card">
                <Image
                  width={320}
                  height={180}
                  src={`${post.coverImage || ""}`}
                  alt={post.name}
                />
                <h4>{post.name}</h4>
              </div>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  useEffect(() => {
    extractH2TagsFromHtml();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blogPost]);

  const handleScroll = () => {
    for (const anchor of h2Tags) {
      const element = document.getElementById(anchor);
      if (element) {
        const rect = element.getBoundingClientRect();
        if (rect.top >= 0 && rect.top < 100) {
          setActiveAnchor(anchor);
          break;
        }
      }
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [h2Tags]);

  useEffect(() => {
    const anchorTags = document.querySelectorAll('[data-href]');
    anchorTags.forEach((a) => {
      a.addEventListener('click', handleAffiliateLinkClick);
    });

    return () => {
      anchorTags.forEach((a) => {
        a.removeEventListener('click', handleAffiliateLinkClick);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postContent]);

  useEffect(() => {
    if (activeAnchor) {
      window.location.hash = activeAnchor;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAnchor]);

  function renderContent(section: IBlogPage['postContent'][0]) {
    switch (section.type) {
      case 'rich-text':
      case 'code':
        return (
          <div dangerouslySetInnerHTML={{
            __html: (section.content || '')
              .replace(/blog.commoninja.com/g, "www.commoninja.com/blog/")
              .replace(
                /https:\/\/www.commoninja.com\/blog\/\/wp-content/g,
                "https://blog.commoninja.com/wp-content"
              )
              .replace(/<pre([^>]*)>([\s\S]*?)<\/pre>/g, '<deckgo-highlight-code><code slot="code">$2</code></deckgo-highlight-code>')
          }
          }
          ></div>
        );

      case 'banner':
        return (
          <Banner
            size={section.bannerSize}
            style={{ padding: '0', margin: '50px 0' }}
            title={section.bannerTitle || ''}
            text={section.bannerText || ''}
            bannerStyle={{ backgroundColor: section.bannerColor, color: (section.bannerColor === '#293258' || section.bannerColor === '#171B2C') ? '#fff' : '#141414', padding: section.bannerSize === 'small' ? '25px 15px' : '50px 30px' }}
            openInNewWindow
            buttonLink={section.bannerButtonLink}
            buttonText={section.bannerButtonText}
          />
        );

      case 'button':
        return (
          <CustomLink
            className={`cta ${section.buttonType}`}
            style={{ color: (section.buttonColor && section.buttonType === 'link') ? section.buttonColor : `var(${CSSVars.primary500})` }}
            href={section.buttonLink}
            target={section.openInNewTab ? "_blank" : '_self'}
          >
            {section.buttonText}
          </CustomLink>
        );

      case 'widget':
        return (
          <>
            {section.pluginId &&
              <div className="html">
                <CommonNinjaWidget widgetId={section.pluginId} />
              </div>
            }
          </>
        );

      case 'quotes':
        return <>
          <div className="quotes-container">
            {section.quotesImg &&
              <img src={section.quotesImg} alt={section.quotesTitle || 'Profile thumbnail'} />
            }
            <p className="title">
              {section.quotesTitle &&
                <span>
                  {section.quotesTitle}
                </span>
              }
              {section.quotesText &&
                <>{section.quotesText}</>
              }
            </p>
          </div>
        </>;

      default:
        return <></>;
    }
  }

  return (
    <Section>
      <StyledDiv>
        <h1>{blogPost.name}</h1>
        <h5>
          {blogPost.author},
          <time dateTime={blogPost.publishedDate}>
            {" "}
            {dayjs(blogPost.publishedDate).format("MMM DD, YYYY")}
          </time>
        </h5>
        <div className="tags">
        </div>
        <div
          className="blog-post-content">
          <>
            {/* <p className="tldr">
              <strong>Summary (TL;DR): </strong>
              {blogPost.summary}
            </p> */}
            <img src={blogPost.coverImage} alt={blogPost.name} className="main-img" />
            {TROFEO_FIFA_SLUGS.has(blogPost.slug) && (
              <TrofeoBanner slug={blogPost.slug} />
            )}
            <div className="content-layout">
              {h2Tags.length > 0 &&
                <div className="side-banner">
                  <div className="table-of-contents">
                    <p className="title">Table of Contents</p>
                    {h2Tags.map((tag, idx) => {
                      return <a
                        key={`${tag}-${idx}`}
                        href={`#${tag}`}
                        className={activeAnchor === tag ? 'active' : ''}
                      >
                        {tag.replaceAll('-', ' ')}
                      </a>;
                    })}
                  </div>
                  {((blogPost.sideBannerButtonText && blogPost.sideBannerButtonLink) && blogPost.sideBannerTitle) &&
                    <div style={{ backgroundColor: blogPost.sideBannerBg }} className="banner">
                      {blogPost.sideBannerCoverImage &&
                        <img src={blogPost.sideBannerCoverImage} alt={"banner cover"} />
                      }
                      {blogPost.sideBannerTitle &&
                        <p style={{ color: blogPost.sideBannerTextColor }} className="title">{blogPost.sideBannerTitle}</p>
                      }
                      {blogPost.sideBannerText &&
                        <p style={{ color: blogPost.sideBannerTextColor }} className="text">{blogPost.sideBannerText}</p>
                      }
                      {(blogPost.sideBannerButtonText && blogPost.sideBannerButtonLink) &&
                        <CustomLink
                          className={`cta`}
                          style={{ color: blogPost.sideBannerButtonColor, backgroundColor: blogPost.sideBannerButtonBg }}
                          href={blogPost.sideBannerButtonLink}
                          target={blogPost.sideBannerOpenInNewTab ? "_blank" : '_self'}
                        >
                          {blogPost.sideBannerButtonText}
                        </CustomLink>
                      }
                    </div>
                  }
                </div>
              }
              <main className="content">
                {postContent.map((section, idx) => (
                  <React.Fragment key={idx}>
                    {renderContent(section)}
                  </React.Fragment>
                ))}
              </main>
            </div>
          </>
        </div>
        {renderMightLikeSection()}
      </StyledDiv>
    </Section>
  );
};
